import { SOCKET_URL } from "@config/socket";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useToastContext } from "./toast";

type WebSocketContextType = {
  isConnected: boolean;
  connectionId?: string;
  sendMessage: (message: string) => void;
};

const enum WebSocketMessageType {
  ConnectionAck = "connection_ack",
  BulkActionCompleted = "bulkActioncompleted",
  BulkActionError = "bulkActionError",
  ConnectionError = "connection_error",
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const socketRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionId, setConnectionId] = useState<string>();
  const { showToast } = useToastContext();

  useEffect(() => {
    socketRef.current = new WebSocket(SOCKET_URL);

    socketRef.current.onopen = () => {
      if (!socketRef.current) return;
      // Send a message to register and get connectionId
      socketRef.current.send(JSON.stringify({ action: "sendMessage", data: "register" }));
      setIsConnected(true);
    };

    socketRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data) as {
        type: WebSocketMessageType;
        connectionId: string;
        message?: string;
      };

      if (data.type === WebSocketMessageType.ConnectionAck) {
        setConnectionId(data.connectionId);
      } else if (data.type === WebSocketMessageType.BulkActionCompleted) {
        showToast({
          title: "Success",
          content: data?.message,
          variant: "success",
        });
      }
    };

    socketRef.current.onclose = () => {
      setIsConnected(false);
    };

    socketRef.current.onerror = (err) => {
      // eslint-disable-next-line no-console
      console.error("[WebSocket] error:", err);
    };

    return () => {
      if (
        socketRef.current?.readyState === WebSocket.OPEN ||
        socketRef.current?.readyState === WebSocket.CONNECTING
      ) {
        socketRef.current.close();
      }
      socketRef.current = null; // 💡 Explicitly reset the ref (optional but safe)
    };
  }, [showToast]);

  const sendMessage = useCallback((msg: string) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(msg);
    } else {
      // eslint-disable-next-line no-console
      console.error("[WebSocket] cannot send, not connected");
    }
  }, []);

  const webSocketValue = useMemo(() => {
    return { isConnected, sendMessage, connectionId };
  }, [isConnected, sendMessage, connectionId]);

  return <WebSocketContext.Provider value={webSocketValue}>{children}</WebSocketContext.Provider>;
};

export const useWebSocket = (): WebSocketContextType => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error("useWebSocket must be used within WebSocketProvider");
  return ctx;
};
